import { useState } from 'react';
import { Lock } from 'lucide-react';

import { TILE_LAYERS, TILE_LAYER_KEYS } from '@/services/map-tiles';
import type { TileLayerKey } from '@/services/map-tiles';
import { SegmentedControl } from '@/components/common/SegmentedControl';
import { CircleCheckbox } from '@/components/common/CircleCheckbox';
import { posthogService } from '@/services/posthog-service';
import { EVENT_MAP_LAYER_TOGGLED } from '@/services/observability/events';

// ─── LayerControl component ───────────────────────────────────────────────────

interface LayerControlProps {
  activeTile: TileLayerKey;
  onTileChange: (key: TileLayerKey) => void;
  showWind: boolean;
  onWindChange: (val: boolean) => void;
  hasWeatherData: boolean;
  showBoundary: boolean;
  onBoundaryChange: (val: boolean) => void;
  hasBoundary: boolean;
  showLocations: boolean;
  onLocationsChange: (val: boolean) => void;
  showPublicLand: boolean;
  onPublicLandChange: (val: boolean) => void;
  /** When true, the Public Land toggle is disabled with an Upgrade to Pro affordance. */
  isPublicLandLocked?: boolean;
  /**
   * Keys of overlay toggles to hide. Valid keys: 'locations', 'wind', 'boundary', 'publicLand'.
   * When a key appears here, that toggle row is not rendered.
   */
  hiddenOverlays?: string[];
  /**
   * Called when a locked toggle is tapped. The parent is responsible for showing an UpsellSheet.
   * featureName will be 'Public Land' or 'Wind Cones'.
   */
  onLockedTap?: (featureName: string) => void;
  /**
   * Controlled open state. When provided, LayerControl operates in controlled mode and
   * calls onOpenChange instead of managing its own state.
   */
  isOpen?: boolean;
  /**
   * Called when the user clicks the icon button. Required when isOpen is provided (controlled mode).
   */
  onOpenChange?: (open: boolean) => void;
}

const TILE_OPTIONS = TILE_LAYER_KEYS.map((key) => ({
  value: key,
  label: TILE_LAYERS[key].name,
}));

export function LayerControl({
  activeTile,
  onTileChange,
  showWind,
  onWindChange,
  hasWeatherData,
  showBoundary,
  onBoundaryChange,
  hasBoundary,
  showLocations,
  onLocationsChange,
  showPublicLand,
  onPublicLandChange,
  isPublicLandLocked = false,
  hiddenOverlays = [],
  onLockedTap,
  isOpen,
  onOpenChange,
}: LayerControlProps) {
  // Uncontrolled fallback — used when isOpen is not provided
  const [internalExpanded, setInternalExpanded] = useState(false);

  // Controlled mode takes precedence when isOpen prop is present
  const effectiveOpen = isOpen !== undefined ? isOpen : internalExpanded;

  function handleToggle() {
    if (isOpen !== undefined && onOpenChange) {
      // Controlled mode — notify parent
      onOpenChange(!effectiveOpen);
    } else {
      // Uncontrolled mode — manage state internally
      setInternalExpanded((v) => !v);
    }
  }

  const hidden = new Set(hiddenOverlays);

  function handleTileChange(key: string) {
    posthogService.track(EVENT_MAP_LAYER_TOGGLED, { layer: key });
    onTileChange(key as TileLayerKey);
  }

  return (
    <div
      data-testid="layer-control"
      style={{ zIndex: 1000 }}
      className="absolute bottom-[200px] right-2 flex flex-col items-end gap-1"
    >
      {/* Expand/collapse toggle */}
      <button
        type="button"
        onClick={handleToggle}
        aria-label={effectiveOpen ? 'Hide layer control' : 'Show layer control'}
        aria-expanded={effectiveOpen}
        className="w-9 h-9 rounded-lg glass-cockpit shadow-card-md flex items-center justify-center text-bark-400 hover:text-bark-200 transition-colors"
      >
        {/* Layers icon */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M2 5.5L8 2.5L14 5.5L8 8.5L2 5.5Z" stroke="currentColor" strokeWidth="1.25" strokeLinejoin="round"/>
          <path d="M2 8.5L8 11.5L14 8.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
          <path d="M2 11.5L8 14.5L14 11.5" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round"/>
        </svg>
      </button>

      {effectiveOpen && (
        <div
          className="glass-cockpit rounded-lg shadow-card-md p-2 min-w-[160px]"
          role="group"
          aria-label="Map layers"
          data-state={effectiveOpen ? 'open' : 'closed'}
        >
          {/* Map Style — SegmentedControl */}
          <p className="field-label px-1 mb-1.5">
            Map Style
          </p>
          <div data-testid="map-style-section" className="mb-3 px-1">
            <SegmentedControl
              options={TILE_OPTIONS}
              value={activeTile}
              onChange={handleTileChange}
            />
          </div>

          {/* Overlays section */}
          <p className="field-label px-1 mb-1 mt-1">
            Overlays
          </p>
          <div className="flex flex-col gap-0.5">
            {/* Locations toggle */}
            {!hidden.has('locations') && (
              <div className="flex items-center gap-1 px-1">
                <CircleCheckbox
                  checked={showLocations}
                  onChange={onLocationsChange}
                  label="Waypoints"
                  inputTestId="layer-toggle-locations"
                />
                <span className="text-body-xs text-bark-300">Waypoints</span>
              </div>
            )}

            {/* Wind toggle */}
            {!hidden.has('wind') && (
              <div
                className={['flex items-center gap-1 px-1', !hasWeatherData ? 'opacity-40' : ''].join(' ')}
                title={hasWeatherData ? undefined : 'Weather data unavailable'}
              >
                <CircleCheckbox
                  checked={showWind && hasWeatherData}
                  onChange={onWindChange}
                  label="Wind"
                  disabled={!hasWeatherData}
                  inputTestId="wind-toggle"
                />
                <span className="text-body-xs text-bark-300">Wind</span>
              </div>
            )}

            {/* Boundary toggle */}
            {!hidden.has('boundary') && (
              <div
                className={['flex items-center gap-1 px-1', !hasBoundary ? 'opacity-40' : ''].join(' ')}
                title={hasBoundary ? undefined : 'No property boundary drawn'}
              >
                <CircleCheckbox
                  checked={showBoundary && hasBoundary}
                  onChange={onBoundaryChange}
                  label="Boundary"
                  disabled={!hasBoundary}
                  inputTestId="boundary-toggle"
                />
                <span className="text-body-xs text-bark-300">Boundary</span>
              </div>
            )}

            {/* Public Land toggle */}
            {!hidden.has('publicLand') && (
              <div
                className={['flex items-center gap-1 px-1', isPublicLandLocked ? 'opacity-40' : ''].join(' ')}
                title={isPublicLandLocked ? 'Upgrade to Pro' : undefined}
                onClick={isPublicLandLocked ? () => { onLockedTap?.('Public Land'); } : undefined}
              >
                <CircleCheckbox
                  checked={showPublicLand}
                  onChange={isPublicLandLocked ? () => { /* locked — click handled by container */ } : onPublicLandChange}
                  label="Public Land"
                  disabled={isPublicLandLocked}
                  inputTestId="layer-toggle-public-land"
                />
                <span className="text-body-xs text-bark-300">Public Land</span>
                {isPublicLandLocked && (
                  <span data-testid="public-land-lock-icon" aria-hidden="true">
                    <Lock size={12} className="text-bark-400" />
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
